import React, { useState, useEffect, useMemo } from 'react';
import { salaryBreakupApi, signatureApi, resolveMediaUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Printer, Save, Pencil, X, History } from 'lucide-react';

/**
 * The HR-F-42 Salary Break-up, as the printed sheet.
 *
 * Deliberately NOT a generic salary form: the same three sections (A gross
 * salary, B other benefits, C the CTC summary), the same five columns
 * (S.No · Particulars · Per Month · Per Annum · Remarks), and the same
 * subtotal lines in the same places.
 *
 * What HR types is ONE figure per manual row, in that row's own basis — the
 * way the paper sheet is filled in. Everything else is derived:
 *   a percentage row (HRA at 50% of Basic) is computed and read-only, so it
 *     can never contradict the rule printed in its own Remarks column;
 *   the other column of the pair is × 12 or ÷ 12;
 *   every total is summed from exact values and rounded once for display.
 *
 * The totals shown while typing are a PREVIEW. The server recomputes from the
 * component master on save and stores that, so the figure on the sheet is
 * never whatever the browser happened to calculate.
 */

const rupee = (v, dp = 2) => (v === null || v === undefined || v === '' ? '—'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }));

/* The same arithmetic the server does — see compute_salary_breakup. Kept in
   step with it deliberately: HR needs the CTC to move as they type, and a
   round trip per keystroke would make the sheet unusable. */
function computeLocal(components, amounts, overrides = {}) {
  const rows = [];
  const byKey = {};
  const ordered = [...components]
    .filter((c) => c.active !== false)
    .sort((a, b) => String(a.section).localeCompare(String(b.section)) || (a.sno || 99) - (b.sno || 99));
  for (const c of ordered) {
    let monthly = 0, annual = 0, row_overridden = false;
    if (c.calc === 'percent') {
      const base = byKey[c.percent_of];
      let baseMonth = base ? base.monthly : 0;
      if (c.percent_base_cap_monthly != null) baseMonth = Math.min(baseMonth, Number(c.percent_base_cap_monthly));
      monthly = baseMonth * (Number(c.percent) || 0) / 100;
      annual = monthly * 12;
      // An override replaces the rule for this sheet only.
      const ov = overrides[c.key];
      if (String(ov ?? '').trim() !== '') {
        monthly = Number(ov) || 0;
        annual = monthly * 12;
        row_overridden = true;
      }
    } else {
      const entered = Number(String(amounts[c.key] ?? '').trim() || 0);
      if (c.basis === 'annual') { annual = entered; monthly = entered / 12; }
      else { monthly = entered; annual = entered * 12; }
    }
    const row = { ...c, monthly, annual, overridden: row_overridden };
    byKey[c.key] = row;
    rows.push(row);
  }
  const sum = (pred, f) => rows.filter(pred).reduce((a, r) => a + r[f], 0);
  const salary = { monthly: sum((r) => r.contributes === 'salary', 'monthly'),
                   annual: sum((r) => r.contributes === 'salary', 'annual') };
  const employer = { monthly: sum((r) => r.contributes === 'employer', 'monthly'),
                     annual: sum((r) => r.contributes === 'employer', 'annual') };
  const benefits = { monthly: sum((r) => r.contributes === 'benefit', 'monthly'),
                     annual: sum((r) => r.contributes === 'benefit', 'annual') };
  const gross = { monthly: salary.monthly + employer.monthly, annual: salary.annual + employer.annual };
  const ctc = { monthly: gross.monthly + benefits.monthly, annual: gross.annual + benefits.annual };
  return { rows, totals: { salary, gross, benefits, ctc } };
}

export function SalaryBreakupSheet({ employeeId, year, canEdit, onSaved }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [amounts, setAmounts] = useState({});
  // Per-sheet figures for percentage rows. Empty = follow the rule.
  const [overrides, setOverrides] = useState({});
  // The company signature, from the signature master — one definition,
  // reused rather than re-uploaded per sheet.
  const [sig, setSig] = useState(null);
  const [remarks, setRemarks] = useState({});
  const [meta, setMeta] = useState({ effective_from: '', declared_ctc_annual: '', rev_no: '', rev_date: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [viewing, setViewing] = useState('');     // a past revision id, or ''

  const load = React.useCallback((revision) => {
    salaryBreakupApi.get(employeeId, year, revision)
      .then((r) => {
        setData(r.data);
        setAmounts(r.data.revision?.amounts || {});
        // Reloaded with the sheet, so reopening "Revise" shows the figures
        // exactly as they were saved — including any hand-set ones.
        setOverrides(r.data.revision?.overrides || {});
        setRemarks(r.data.revision?.remarks || {});
        setMeta({
          effective_from: r.data.revision?.effective_from || `${year}-04-01`,
          declared_ctc_annual: r.data.revision?.declared_ctc_annual ?? '',
          rev_no: r.data.revision?.rev_no || '',
          rev_date: r.data.revision?.rev_date || '',
          note: '',
          prepared_by: r.data.revision?.prepared_by || '',
          accepted_by: r.data.revision?.accepted_by || '',
          accepted_signature: r.data.revision?.accepted_signature ?? true,
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load the salary break-up'));
  }, [employeeId, year]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    signatureApi.get().then((r) => setSig(r.data || null)).catch(() => setSig(null));
  }, []);

  const components = data?.components || [];
  // While editing, the sheet follows what is being typed. While reading, it
  // shows exactly what the server stored for this revision.
  const live = useMemo(
    () => (editing ? computeLocal(components, amounts, overrides) : null),
    [editing, components, amounts, overrides]);
  const rows = live ? live.rows : (data?.rows || []);
  const totals = live ? live.totals : data?.totals;

  const save = async () => {
    setBusy(true);
    try {
      const r = await salaryBreakupApi.save(employeeId, {
        year,
        amounts: Object.fromEntries(Object.entries(amounts)
          .filter(([, v]) => String(v ?? '').trim() !== '')
          .map(([k, v]) => [k, Number(v)])),
        remarks,
        overrides: Object.fromEntries(Object.entries(overrides)
          .filter(([, v]) => String(v ?? '').trim() !== '')
          .map(([k, v]) => [k, Number(v)])),
        prepared_by: meta.prepared_by || null,
        accepted_by: meta.accepted_by || null,
        accepted_signature: !!meta.accepted_signature,
        effective_from: meta.effective_from || undefined,
        declared_ctc_annual: meta.declared_ctc_annual === '' ? null : Number(meta.declared_ctc_annual),
        rev_no: meta.rev_no, rev_date: meta.rev_date, note: meta.note,
      });
      setData(r.data);
      setEditing(false); setViewing('');
      toast.success(`${year} salary break-up saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  const openSheet = async () => {
    const w = window.open('', '_blank');
    try {
      const r = await salaryBreakupApi.sheet(employeeId, year, viewing || undefined,
                                             `${window.location.origin}/assets/aampl-logo.jpg`);
      if (w) { w.document.write(r.data.html); w.document.close(); }
    } catch (e) {
      if (w) w.close();
      toast.error(e?.response?.data?.detail || 'Nothing to print yet');
    }
  };

  if (!data) return <div className="text-sm text-[#5A6377] py-6">Loading…</div>;

  const emp = data.employee;
  const pending = data.status === 'pending' && !editing;
  const secA = rows.filter((r) => r.section === 'A');
  const lastSalaryRow = [...secA].filter((r) => r.contributes === 'salary').slice(-1)[0];

  const cellN = 'text-right tabular-nums whitespace-nowrap px-2 py-1.5 border border-[#C7CEDB]';
  const cellT = 'px-2 py-1.5 border border-[#C7CEDB]';
  const input = 'w-full text-right tabular-nums border border-[#D7E3FF] rounded px-1.5 py-0.5 text-sm';

  const amountCell = (r) => {
    if (!editing) {
      return (
        <td className={cellN}>
          {r.basis === 'annual' ? rupee(r.annual, 0) : rupee(r.monthly)}
          {/* Marked, so a figure that no longer follows the printed rule is
              never mistaken for one that does. */}
          {r.overridden && <span className="ml-1 text-[10px] text-[#8A5A12]" title="Set by hand, not the rule">*</span>}
        </td>
      );
    }
    /* A PERCENTAGE row is computed from the rule beside it, which is right
       until the one offer that genuinely differs — an HRA agreed at other
       than 50% of Basic. Typing here overrides it FOR THIS SHEET; the master
       keeps its rule and every other sheet still follows it. Clearing the
       box hands the row back to the rule. */
    if (r.calc === 'percent') {
      const ov = overrides[r.key];
      const on = String(ov ?? '').trim() !== '';
      return (
        <td className={`${cellN} ${on ? 'bg-[#FFFBEB]' : 'bg-[#F7FAFF]'}`}>
          <div className="flex items-center gap-1 justify-end">
            <input type="number" min="0" step="0.01"
                   value={on ? ov : Number(r.monthly).toFixed(2)}
                   data-testid={`sb-override-${r.key}`}
                   title={on ? `Overrides ${r.remark || 'the rule'} — clear to restore it`
                             : r.remark || 'Computed from the rule'}
                   onChange={(e) => setOverrides((s) => ({ ...s, [r.key]: e.target.value }))}
                   className={`${input} ${on ? 'border-[#E9A23B]' : ''}`} />
            {on && (
              <button type="button" title="Back to the rule"
                      data-testid={`sb-override-reset-${r.key}`}
                      onClick={() => setOverrides((s) => {
                        const n = { ...s }; delete n[r.key]; return n;
                      })}
                      className="text-[#8A5A12] text-xs leading-none px-1">↺</button>
            )}
          </div>
        </td>
      );
    }
    return (
      <td className={`${cellN} bg-[#F7FAFF]`}>
        <input type="number" min="0" step="0.01" value={amounts[r.key] ?? ''}
               data-testid={`sb-amount-${r.key}`}
               onChange={(e) => setAmounts((s) => ({ ...s, [r.key]: e.target.value }))}
               className={input} />
      </td>
    );
  };

  /* One figure is typed per row — in that row's OWN basis, like the paper
     sheet — and the other column is derived. Letting both be typed would
     allow a row whose monthly and annual disagree. */
  const row = (r) => (
    <tr key={r.key} data-testid={`sb-row-${r.key}`}>
      <td className={`${cellT} text-center w-10`}>{r.sno}</td>
      <td className={cellT}>{r.name}</td>
      {r.basis === 'annual'
        ? <td className={cellN}>{rupee(r.monthly)}</td>
        : amountCell(r)}
      {r.basis === 'annual'
        ? amountCell(r)
        : <td className={cellN}>{rupee(r.annual, 0)}</td>}
      <td className={`${cellT} text-[11px] text-[#5A6377]`}>
        {editing ? (
          <input value={remarks[r.key] ?? r.remark ?? ''}
                 data-testid={`sb-remark-${r.key}`}
                 onChange={(e) => setRemarks((s) => ({ ...s, [r.key]: e.target.value }))}
                 className="w-full border border-[#D7E3FF] rounded px-1.5 py-0.5 text-[11px]" />
        ) : (remarks[r.key] ?? r.remark ?? '')}
      </td>
    </tr>
  );

  const band = (label, t, note) => (
    <tr className="bg-[#FFF7CC] font-semibold">
      <td className={cellT} />
      <td className={cellT}>{label}</td>
      <td className={cellN}>{rupee(t.monthly)}</td>
      <td className={cellN}>{rupee(t.annual, 0)}</td>
      <td className={`${cellT} text-[11px]`}>{note || ''}</td>
    </tr>
  );

  return (
    <div data-testid="salary-breakup-tab" className="space-y-4">
      {/* ---- document header, exactly as the sheet prints it ---- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-base font-semibold text-[#11182B]">{year} — Salary Break-up</div>
          <div className="text-[11px] text-[#98A1B2]">
            Doc No: {data.doc_no} · Rev No: {data.revision?.rev_no || '0'}
            {data.revision?.rev_date ? ` · Rev.Date: ${data.revision.rev_date}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.history?.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setShowHistory((v) => !v)}
                    data-testid="sb-history-toggle">
              <History className="w-4 h-4 mr-1" />History ({data.history.length})
            </Button>
          )}
          {!editing && data.revision && (
            <Button size="sm" variant="outline" onClick={openSheet} data-testid="sb-print">
              <Printer className="w-4 h-4 mr-1" />Preview / PDF
            </Button>
          )}
          {canEdit && data.can_edit && !editing && (
            <Button size="sm" onClick={() => setEditing(true)} data-testid="sb-edit">
              <Pencil className="w-4 h-4 mr-1" />{data.revision ? 'Revise' : 'Fill the sheet'}
            </Button>
          )}
          {editing && (
            <>
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => { setEditing(false); load(viewing || undefined); }}>
                <X className="w-4 h-4 mr-1" />Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={busy} data-testid="sb-save">
                <Save className="w-4 h-4 mr-1" />{busy ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ---- employee details, read from the employee record ---- */}
      <table className="w-full text-sm border-collapse">
        <tbody>
          <tr>
            <td className={`${cellT} w-28 font-semibold bg-[#F7F8FB]`}>Name</td>
            <td className={cellT} data-testid="sb-emp-name">{emp.name || '—'}</td>
            <td className={`${cellT} w-36 font-semibold bg-[#F7F8FB]`}>CTC/Annum in Rs.</td>
            <td className={cellN} data-testid="sb-header-ctc">
              {totals ? rupee(totals.ctc.annual, 0) : '—'}
            </td>
          </tr>
          <tr>
            <td className={`${cellT} font-semibold bg-[#F7F8FB]`}>Designation</td>
            <td className={cellT} data-testid="sb-emp-designation">{emp.designation || '—'}</td>
            <td className={`${cellT} font-semibold bg-[#F7F8FB]`}>Date of joining</td>
            <td className={cellN} data-testid="sb-emp-doj">{emp.doj || '—'}</td>
          </tr>
          <tr>
            <td className={`${cellT} font-semibold bg-[#F7F8FB]`}>Department</td>
            <td className={cellT} colSpan={3} data-testid="sb-emp-department">{emp.department || '—'}</td>
          </tr>
        </tbody>
      </table>

      {pending ? (
        /* Not a sheet of zeros. A salary nobody has entered must not read as
           a salary of nothing. */
        <div data-testid="sb-pending"
             className="border border-[#FDD08A] bg-[#FFF7E6] text-[#8A5A12] rounded-[10px] px-4 py-3 text-sm">
          <b>Pending.</b> No salary break-up has been entered for {year} yet.
          {canEdit && data.can_edit ? ' Use “Fill the sheet” to enter it.'
            : ' It can be completed by HR when the figures are known.'}
        </div>
      ) : (
        <>
          <table className="w-full text-sm border-collapse" data-testid="sb-sheet">
            <thead>
              <tr className="bg-[#EAF0F6] text-[11px] uppercase">
                <th className={`${cellT} w-10`}>S.No</th>
                <th className={`${cellT} text-left`}>Particulars</th>
                <th className={`${cellT} w-32`}>Per Month ( Rs.)</th>
                <th className={`${cellT} w-32`}>Per Annum ( Rs.)</th>
                <th className={`${cellT} text-left`}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-[#F2F4F8] font-semibold">
                <td className={cellT} colSpan={5}>A) Gross salary</td>
              </tr>
              {secA.map((r) => (
                <React.Fragment key={r.key}>
                  {row(r)}
                  {/* The "Salary" subtotal sits between rows 5 and 6 on the
                      sheet — before the employer contributions are added. */}
                  {lastSalaryRow && r.key === lastSalaryRow.key && totals && (
                    <tr className="font-semibold">
                      <td className={cellT} />
                      <td className={cellT}>Salary</td>
                      <td className={cellN}>{rupee(totals.salary.monthly)}</td>
                      <td className={cellN}>{rupee(totals.salary.annual, 0)}</td>
                      <td className={cellT} />
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {totals && band('Gross Salary ( 1-7)', totals.gross, 'WITH EMPLOYER PF CONTRIBUTION')}

              <tr className="bg-[#F2F4F8] font-semibold">
                <td className={cellT} colSpan={5}>B) OTHER BENEFITS</td>
              </tr>
              {rows.filter((r) => r.section === 'B').map(row)}
              {totals && band('Total Benefits', totals.benefits)}

              <tr className="bg-[#F2F4F8] font-semibold">
                <td className={cellT} colSpan={5}>C) CTC</td>
              </tr>
              {totals && (
                <>
                  <tr>
                    <td className={`${cellT} text-center`}>(A)</td>
                    <td className={cellT}>Gross Salary</td>
                    <td className={cellN}>{rupee(totals.gross.monthly)}</td>
                    <td className={cellN}>{rupee(totals.gross.annual, 0)}</td>
                    <td className={cellT} />
                  </tr>
                  <tr>
                    <td className={`${cellT} text-center`}>(B)</td>
                    <td className={cellT}>Other Benefits</td>
                    <td className={cellN}>{rupee(totals.benefits.monthly)}</td>
                    <td className={cellN}>{rupee(totals.benefits.annual, 0)}</td>
                    <td className={cellT} />
                  </tr>
                  <tr className="bg-[#FFF200] font-bold" data-testid="sb-total-ctc">
                    <td className={cellT} />
                    <td className={cellT}>Total CTC</td>
                    <td className={cellN}>{rupee(totals.ctc.monthly)}</td>
                    <td className={cellN}>{rupee(totals.ctc.annual, 0)}</td>
                    <td className={cellT} />
                  </tr>
                </>
              )}
            </tbody>
          </table>

          {editing && (
            <div className="flex flex-wrap gap-4 items-end bg-[#F7F8FB] border border-[#EEF1F6] rounded-xl px-4 py-3">
              <label className="text-xs text-[#5A6377]">
                <div className="mb-1 font-semibold">Effective from</div>
                <input type="date" value={meta.effective_from} data-testid="sb-effective-from"
                       onChange={(e) => setMeta((m) => ({ ...m, effective_from: e.target.value }))}
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm" />
              </label>
              <label className="text-xs text-[#5A6377]">
                <div className="mb-1 font-semibold">Rev No</div>
                <input value={meta.rev_no} onChange={(e) => setMeta((m) => ({ ...m, rev_no: e.target.value }))}
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm w-20" />
              </label>
              <label className="text-xs text-[#5A6377]">
                <div className="mb-1 font-semibold">Rev Date</div>
                <input type="date" value={meta.rev_date}
                       onChange={(e) => setMeta((m) => ({ ...m, rev_date: e.target.value }))}
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm" />
              </label>
              <label className="text-xs text-[#5A6377]">
                {/* Optional cross-check. If it disagrees with the components
                    the save is REFUSED and the difference named — nothing is
                    nudged to make the columns agree. */}
                <div className="mb-1 font-semibold">Declared CTC / annum (optional check)</div>
                <input type="number" value={meta.declared_ctc_annual} data-testid="sb-declared-ctc"
                       onChange={(e) => setMeta((m) => ({ ...m, declared_ctc_annual: e.target.value }))}
                       placeholder="leave blank to skip"
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm w-44" />
              </label>
              <label className="text-xs text-[#5A6377] flex-1 min-w-[180px]">
                <div className="mb-1 font-semibold">Note on this revision</div>
                <input value={meta.note} onChange={(e) => setMeta((m) => ({ ...m, note: e.target.value }))}
                       placeholder="e.g. Annual increment 2026"
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm w-full" />
              </label>
              {/* Who drew it up and who approved it. The signature is taken
                  from the signature master rather than uploaded here, so one
                  person's signature has exactly one definition. */}
              <label className="text-xs text-[#5A6377]">
                <div className="mb-1 font-semibold">Prepared by</div>
                <input value={meta.prepared_by || ''} data-testid="sb-prepared-by"
                       onChange={(e) => setMeta((m) => ({ ...m, prepared_by: e.target.value }))}
                       placeholder="name"
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm w-44" />
              </label>
              <label className="text-xs text-[#5A6377]">
                <div className="mb-1 font-semibold">Accepted by</div>
                <input value={meta.accepted_by || ''} data-testid="sb-accepted-by"
                       onChange={(e) => setMeta((m) => ({ ...m, accepted_by: e.target.value }))}
                       placeholder={sig?.name || 'name'}
                       className="border border-[#D7E3FF] rounded px-2 py-1 text-sm w-44" />
              </label>
              {sig?.url && (
                <label className="text-xs text-[#5A6377] flex items-center gap-2">
                  <input type="checkbox" checked={!!meta.accepted_signature}
                         data-testid="sb-accepted-signature"
                         onChange={(e) => setMeta((m) => ({ ...m, accepted_signature: e.target.checked }))}
                         className="w-4 h-4 accent-[#2563EB]" />
                  <span>
                    <span className="font-semibold block">Print signature</span>
                    <span className="text-[#98A1B2]">{sig.name}{sig.title ? ` · ${sig.title}` : ''}</span>
                  </span>
                </label>
              )}
            </div>
          )}

          {/* The signature block, as it prints. Shown whenever either name is
              recorded, so the saved sheet reads the same on screen as on paper. */}
          {!editing && (data.revision?.prepared_by || data.revision?.accepted_by) && (
            <div className="flex flex-wrap justify-between gap-8 mt-6 pt-4"
                 data-testid="sb-signoff">
              <div className="text-sm">
                <div className="h-12" />
                <div className="border-t border-[#11182B] pt-1 min-w-[180px]">
                  <span className="text-[11px] text-[#5A6377] block">Prepared by</span>
                  <span className="font-medium">{data.revision.prepared_by || '—'}</span>
                </div>
              </div>
              <div className="text-sm">
                <div className="h-12 flex items-end">
                  {data.revision.accepted_signature && sig?.url && (
                    <img src={resolveMediaUrl(sig.url)} alt={sig.name || 'signature'}
                         data-testid="sb-signature-img"
                         className="max-h-12 object-contain" />
                  )}
                </div>
                <div className="border-t border-[#11182B] pt-1 min-w-[180px]">
                  <span className="text-[11px] text-[#5A6377] block">Accepted by</span>
                  <span className="font-medium">{data.revision.accepted_by || sig?.name || '—'}</span>
                  {sig?.title && data.revision.accepted_signature && (
                    <span className="text-[11px] text-[#5A6377] block">{sig.title}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- revisions. Nothing is overwritten, so this is also the audit. ---- */}
      {showHistory && (
        <table className="w-full text-sm mt-2" data-testid="sb-history">
          <thead>
            <tr className="text-[11px] uppercase text-[#98A1B2] border-b border-[#F2F4F8]">
              <th className="text-left py-2">Effective from</th>
              <th className="text-left py-2">Rev</th>
              <th className="text-right py-2">CTC / annum</th>
              <th className="text-left py-2">Entered by</th>
              <th className="text-left py-2">Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.history.map((h) => (
              <tr key={h.id} className="border-b border-[#F7F8FB]">
                {/* Marked by ID, not by position — two revisions can share an
                    effective date, and "the first row" is not the same thing
                    as "the one in force". */}
                <td className="py-1.5">{h.effective_from}
                  {h.id === data.revision?.id && (
                    <span className="ml-2 text-[10px] font-semibold text-[#15654B]">CURRENT</span>
                  )}
                </td>
                <td className="py-1.5 text-[#5A6377]">{h.rev_no || '—'}</td>
                <td className="py-1.5 text-right tabular-nums">{rupee(h.ctc_annual, 0)}</td>
                <td className="py-1.5 text-[#5A6377]">{h.created_by || '—'}</td>
                <td className="py-1.5 text-[#5A6377]">{h.note || '—'}</td>
                <td className="py-1.5 text-right">
                  <button className="text-[#2563EB] text-xs hover:underline"
                          data-testid={`sb-view-${h.id}`}
                          onClick={() => { setViewing(h.id); setEditing(false); load(h.id); }}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {viewing && !editing && (
        <div className="text-xs text-[#8A5A12] bg-[#FFF7E6] border border-[#FDD08A] rounded px-3 py-2">
          Viewing an earlier revision.{' '}
          <button className="underline" onClick={() => { setViewing(''); load(); }}>
            Back to the current one
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Year-wise Salary Breakup — the section that sits under Salary Progression.
 *
 * The YEARS are the salary history's own years; this keeps no list of its
 * own, so a year cannot exist in one place and not the other. Each one opens
 * its own HR-F-42 sheet, and each is an independent approved structure:
 * revising 2027 leaves 2026 and 2025 exactly as they were.
 *
 * A year with a gross recorded against it is NOT therefore "done". The gross
 * is one number; the sheet is eleven. Until somebody fills it in, the year
 * reads Pending.
 */
export default function SalaryBreakupYears({ employeeId, canEdit }) {
  const [index, setIndex] = useState(null);
  const [open, setOpen] = useState(null);          // the year expanded, or null

  const load = React.useCallback(() => {
    salaryBreakupApi.years(employeeId)
      .then((r) => setIndex(r.data))
      .catch(() => setIndex(null));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  if (!index) return null;
  const years = index.years || [];

  return (
    <div className="mt-8 border-t border-[#E6E9F0] pt-6" data-testid="salary-breakup-years">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h3 className="text-base font-semibold text-[#11182B]">Year-wise Salary Breakup</h3>
        <span className="text-[11px] text-[#98A1B2]">Doc No: HR-F-42</span>
      </div>
      <p className="text-[12px] text-[#5A6377] mb-4">
        One HR-F-42 sheet per salary year, taken from the years above. Each year is its own
        approved structure — revising a later year never changes an earlier one.
      </p>

      {years.length === 0 ? (
        <div className="text-sm text-[#98A1B2]">
          No salary years recorded yet. Add a year in Salary Progression and its break-up
          can be filled in here.
        </div>
      ) : years.map((y) => (
        <div key={y.year} data-testid={`sby-row-${y.year}`}
             className="border border-[#E6E9F0] rounded-xl mb-3 overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white">
            <span className="text-lg font-bold text-[#172554] tabular-nums w-16">{y.year}</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
              y.status === 'completed' ? 'bg-[#EAF7F0] text-[#15654B] border-[#CDEBDB]'
                                       : 'bg-[#FFF7E6] text-[#8A5A12] border-[#FDD08A]'}`}
                  data-testid={`sby-status-${y.year}`}>
              {y.status === 'completed' ? 'Completed' : 'Salary Breakup Pending'}
            </span>
            <span className="text-[12px] text-[#5A6377]">
              {y.gross != null && <>Progression gross <b className="text-[#11182B]">{rupee(y.gross, 0)}</b>/mo</>}
              {y.ctc_annual != null && <> · CTC <b className="text-[#11182B]">{rupee(y.ctc_annual, 0)}</b>/annum</>}
              {y.revisions > 1 && <> · {y.revisions} revisions</>}
            </span>
            <span className="flex-1" />
            <Button size="sm" variant={open === y.year ? 'default' : 'outline'}
                    data-testid={`sby-open-${y.year}`}
                    onClick={() => setOpen(open === y.year ? null : y.year)}>
              {open === y.year ? 'Close'
                : y.status === 'completed' ? 'View / Edit'
                  : (canEdit && index.can_edit ? 'Create Salary Breakup' : 'View')}
            </Button>
          </div>

          {/* Stated, never acted on: the software does not pick a winner
              between the two figures. */}
          {y.mismatch && (
            <div data-testid={`sby-mismatch-${y.year}`}
                 className="px-4 py-2 text-[12px] bg-[#FFF7E6] border-t border-[#FDD08A] text-[#8A5A12]">
              {y.mismatch}
            </div>
          )}

          {open === y.year && (
            <div className="border-t border-[#E6E9F0] bg-[#FBFCFE] px-4 py-4">
              <SalaryBreakupSheet employeeId={employeeId} year={y.year}
                                  canEdit={canEdit} onSaved={load} />
            </div>
          )}
        </div>
      ))}

      {(index.orphans || []).length > 0 && (
        <div className="text-[12px] text-[#8A5A12] bg-[#FFF7E6] border border-[#FDD08A] rounded-lg px-3 py-2">
          A salary break-up exists for {index.orphans.join(', ')}, but that year is no longer in
          Salary Progression. Nothing has been deleted — add the year back, or leave it as history.
        </div>
      )}
    </div>
  );
}
